import React, { Fragment } from 'react';
import HeaderMeta from 'components/headerMeta/HeaderMeta';
import HubspotForm from 'pages/commen/components/HubspotForm';
import styled from 'styled-components';
import { vw } from 'components/common/Common';
import { H1, H3, P } from 'pages/jetCard/JetCard.style';
import ImgDescCard from 'components/ImgDescCard';
import {
  IconDescItemDesc,
  IconDesc,
  IconDescItem,
  Separator,
} from 'pages/charterQuote/components/Description';
import Header from 'pages/charterQuote/components/contents/Header';
import Partner from 'pages/affiliate/components/Partner';
import Commission from 'pages/affiliate/components/Commission';
import WhyChoose from 'pages/affiliate/components/WhyChoose';
import FAQAccordion from 'pages/charterQuote/components/contents/FAQAccordion';
import FooterHeader from 'pages/jet/components/Header';
import Footer from 'components/layout/Footer';

const TopHeader = styled.div`
  width: 100%;
  height: fit-content;
  margin: 0 auto;
  background-color: #fff;
  padding: ${(props) => props.$padding || `0 0`};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Left = styled.div`
  max-width: 746px;
`;

const Right = styled.div`
  max-width: ${vw(420)};
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${vw(30)};

  p {
    color: #424748;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0;
  }
`;

const Content = styled.div`
  width: 100%;
  height: fit-content;
  padding: 0 ${vw(80)} ${vw(200)};
  display: flex;
  flex-direction: column;
  gap: 200px;
`;

const Form = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${vw(40)};
`;

const ImgWrapper = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HubspotFormWrapper = styled.div`
  width: 50%;
  display: flex;
  align-items: flex-start;

  & > div {
    width: 100%;
  }
`;

const DescBox = styled.span`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 20px;
  line-height: normal;
  color: #050a30;
`;

const Desc = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 1.3;
  color: #424748;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;
`;

const headerTitle = 'Join The Airacer Private Jet Affiliate Program';
const headerParagraphs = [
  'Earn high-ticket commissions, track client requests, and grow recurring income.',
  'All powered by AI. ',
];

const headerTitle2 = 'How the Airacer Affiliate Program Works';
const headerParagraphs2 = [
  'Airacer’s Private Jet Affiliate Program lets brokers, influencers, and travel professionals earn up to 30% commission on every confirmed booking.',
];

const HeaderSection = ({ title, paragraphs, padding }) => {
  return (
    <TopHeader style={{ padding }}>
      <Left>
        <H1>{title}</H1>
      </Left>
      <Right>
        {paragraphs.map((paragraph, index) => (
          <P key={index}>{paragraph}</P>
        ))}
      </Right>
    </TopHeader>
  );
};

const IconTypeDesc = ({ items = [] }) => {
  return (
    <IconDesc>
      {items.map((item, index) => (
        <Fragment key={index}>
          <IconDescItem>
            <img src={item.src} alt={item.alt || ''} />
            <DescBox>
              <Title>{item.title}</Title>
              <Desc>{item.desc}</Desc>
            </DescBox>
          </IconDescItem>
          {index < items.length - 1 && <Separator />}
        </Fragment>
      ))}
    </IconDesc>
  );
};

const howItWorksItems = [
  {
    src: '/imgs/svg/charterQuote/confirm-book.svg',
    alt: 'Referral',
    title: 'Referral',
    desc: (
      <IconDescItemDesc>
        Share your unique affiliate link, banner, or widget with your clients or audience. When
        someone books through your link, you earn a commission.
      </IconDescItemDesc>
    ),
  },
  {
    src: '/imgs/svg/charterQuote/ticket.svg',
    alt: 'Commission',
    title: 'Commission',
    desc: (
      <IconDescItemDesc>
        Earn up to 30% of net profit per booking. The more deals you close each month, the higher
        your rate. You also receive recurring revenue when your referred clients book again within
        12 months.
      </IconDescItemDesc>
    ),
  },
  {
    src: '/imgs/svg/charterQuote/plane-car.svg',
    alt: 'Tracking',
    title: 'Tracking',
    desc: (
      <IconDescItemDesc>
        {
          'The Affiliate Dashboard simplifies tracking so you can focus on growing your network and earning more. Get clear insights, and access transparent commission details whenever you need them.'
        }
      </IconDescItemDesc>
    ),
  },
];

const QuestionLists = [
  {
    question: 'How much can I earn with the Airacer Affiliate Program?',
    answer:
      'You can earn up to 30% of the net profit per booking, plus recurring commissions for 12 months on repeat clients.',
  },
  {
    question: 'What makes Airacer different from other private jet affiliate programs?',
    answer:
      'Airacer’s platform is powered by AI automation and real aviation data. Brokers can instantly source aircraft, view pricing, and track requests.',
  },
  {
    question: 'Who can join the program?',
    answer:
      'We welcome brokers, travel agents, influencers, and aviation businesses. If your audience includes high-end travelers or corporate clients, this program is designed for you.',
  },
  {
    question: 'How fast do payouts happen?',
    answer:
      'Commissions are processed within 24 hours of flight completion via PayPal or bank transfer.',
  },
  {
    question: 'Does Airacer provide marketing materials?',
    answer:
      'Yes. Affiliates receive banners, widgets, and ready-to-use templates to start promoting right away.',
  },
];

const Affiliate = () => {
  return (
    <>
      <HeaderMeta type="affiliate" />

      <HeaderSection
        title={headerTitle}
        paragraphs={headerParagraphs}
        padding={`${vw(150)} ${vw(80)} ${vw(80)}`}
      />

      <Content>
        <Form>
          <ImgWrapper>
            <Img
              src="/imgs/webp/affiliate/private-jet.webp"
              alt="Private jet on sunlit tarmac near city skyline, evoking premium travel, comfort, and exclusivity."
            />
          </ImgWrapper>
          <HubspotFormWrapper>
            <HubspotForm
              scriptSrc="https://js.hsforms.net/forms/embed/6129878.js"
              formId="6fafbf2d-af33-44b7-8080-68fdd9cd3ce6"
            />
          </HubspotFormWrapper>
        </Form>

        <Row>
          <Header title={headerTitle2} desc={headerParagraphs2} />

          <ImgDescCard
            isLeft={false}
            imgSrc="/imgs/webp/affiliate/luggage.webp"
            imgAlt="Stylish traveler with luggage beside a private jet on an empty runway, symbolizing luxury air travel."
          >
            <IconTypeDesc items={howItWorksItems} />
          </ImgDescCard>
        </Row>

        <Partner />

        <Commission />

        <WhyChoose />

        <ImgDescCard
          isLeft={false}
          imgSrc="/imgs/webp/charterQuote/questions.webp"
          imgAlt="private jet preparing for takeoff on the tarmac"
          title="Frequently Asked Questions"
        >
          <FAQAccordion QuestionLists={QuestionLists} />
        </ImgDescCard>
      </Content>

      <FooterHeader
        imgUrl="/imgs/webp/jet/bottom.webp"
        titleContent={<H3 style={{ color: '#fff', fontSize: '48px' }}>Ready to Join?</H3>}
        descContent={
          <>
            <P $color="#fff">
              Join hundreds of brokers and travel professionals earning recurring income with
              Airacer.
              <br />
              Apply to start promoting private jet charters
              <br />
              Let us help you close your next deal faster than ever with the power of AI.
            </P>
          </>
        }
        height={'400'}
      />
      <Footer />
    </>
  );
};

export default Affiliate;
